import { KiaAssistantSDK } from './sdk/KiaAssistantSDK';
import { KiaAssistantError, type AssistantState, type KiaAssistantConfig } from './types';

/**
 * Module-scoped singleton — the ad creative page hosts exactly one
 * KiaAssistant widget, so no global `window` state is needed beyond the
 * single `KiaAssistant` export itself (or `window.KiaAssistant` in the UMD
 * build, set up by the bundler).
 */
let instance: KiaAssistantSDK | null = null;

function requireInstance(method: string): KiaAssistantSDK {
  if (!instance) {
    throw new KiaAssistantError(
      `KiaAssistant.${method}() was called before KiaAssistant.init().`,
      'NOT_INITIALIZED',
    );
  }
  return instance;
}

export const KiaAssistant = {
  /**
   * Validates the three-element config, binds the SDK to those elements,
   * and wires internal state/mic/output/audio modules. Throws
   * KiaAssistantError if config is invalid or the SDK is already
   * initialized (call destroy() first to re-initialize).
   */
  init(config: KiaAssistantConfig): void {
    if (instance) {
      throw new KiaAssistantError(
        'KiaAssistant.init() was called while already initialized. Call destroy() first.',
        'ALREADY_INITIALIZED',
      );
    }
    instance = new KiaAssistantSDK(config);
  },

  open(): void {
    requireInstance('open').open();
  },

  close(): void {
    requireInstance('close').close();
  },

  reset(): void {
    requireInstance('reset').reset();
  },

  destroy(): void {
    if (!instance) return;
    instance.destroy();
    instance = null;
  },

  /** Current internal lifecycle state, primarily for tests/diagnostics. */
  getState(): AssistantState | null {
    return instance ? instance.getState() : null;
  },
};

export type {
  AssistantResponse,
  AssistantState,
  DisplayType,
  KiaAssistantConfig,
  LocationContext,
  LocationPrecision,
  LocationSource,
  PermissionOutcome,
} from './types';
export { KiaAssistantError } from './types';
export type { AudioOutputProvider } from './audio/AudioOutput';
export { NoopAudioOutputProvider } from './audio/AudioOutput';
export { SpeechSynthesisAudioOutputProvider } from './audio/SpeechSynthesisAudioOutputProvider';
export { CloudTextToSpeechAudioOutputProvider } from './audio/CloudTextToSpeechAudioOutputProvider';
export type { LocationProvider } from './location/LocationProvider';
export { BrowserGpsLocationProvider } from './location/BrowserGpsLocationProvider';

/**
 * Also the default export — required so the UMD build's global
 * (window.KiaAssistant) is this API object directly rather than a
 * namespace wrapping it alongside the other named exports. See
 * vite.config.ts's `output.exports: 'default'`. ESM consumers can use
 * either `import KiaAssistant from '...'` or `import { KiaAssistant } from '...'`.
 */
export default KiaAssistant;
